import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';
import { getDocsTree, type DocTreeNode } from '@/lib/docs';
import {
  getDocLabel,
  getSectionMeta,
  sectionUrl,
} from '@/lib/docSections';

interface HubCard {
  name: string;
  path: string;
}

const findNode = (
  nodes: DocTreeNode[],
  segments: string[]
): DocTreeNode | null => {
  if (!segments.length) return null;
  let level = nodes;
  let node: DocTreeNode | null = null;
  for (const segment of segments) {
    const found: DocTreeNode | undefined = level.find(n => n.name === segment);
    if (!found) return null;
    node = found;
    level = found.children ?? [];
  }
  return node;
};

interface SectionHubProps {
  sectionPath: string;
}

const SectionHub: React.FC<SectionHubProps> = ({ sectionPath }) => {
  const [cards, setCards] = useState<HubCard[]>([]);
  const meta = getSectionMeta(sectionPath);
  const url = sectionUrl(sectionPath);

  useEffect(() => {
    let cancelled = false;

    getDocsTree().then(tree => {
      if (cancelled) return;

      const segments = sectionPath ? sectionPath.split('/') : [];
      const node = sectionPath ? findNode(tree, segments) : null;
      const children = sectionPath ? node?.children ?? [] : tree;

      setCards(
        children.map(child => ({
          path: child.path,
          name: getDocLabel(child.path),
        }))
      );
    });

    return () => {
      cancelled = true;
    };
  }, [sectionPath]);

  return (
    <div className="space-y-8 pb-6">
      <SEOMetadata
        title={meta.pageTitle ?? `${meta.label} - AxionQuant API Documentation`}
        description={meta.description}
        canonicalUrl={`https://axionquant.com${url}`}
      />
      <div className="mb-2">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">
          {meta.label}
        </DocHeading>
        {meta.intro && (
          <p className="text-lg text-gray-700 max-w-[900px]">{meta.intro}</p>
        )}
      </div>

      {cards.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 max-w-[1200px]">
          {cards.map(card => (
            <Link
              key={card.path}
              to={`/docs/${card.path}`}
              className="group flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 transition-colors hover:border-gray-300 hover:bg-gray-50"
            >
              <span className="text-sm font-semibold text-gray-900 group-hover:text-black">
                {card.name}
              </span>
              <span
                aria-hidden="true"
                className="text-gray-400 transition-colors group-hover:text-gray-600"
              >
                &rarr;
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default SectionHub;
